import Head from 'next/head'
import Script from 'next/script'
import { Handyman } from '../../../components/index/service-category/handyman/Handyman';
import { contentDate } from '../../../utils/article-date-time';
import { handymanTestimonials } from '../../../data/CustomerReviewData';
import { buildBreadcrumb } from 'utils/breadcrumb'
import { buildServiceHubGraph } from 'utils/service-hub-schema'
import { formatPrice, fromPrice, loadHandymanCategorySettings, loadHandymanPriceList } from 'utils/handyman-price-data'
import { HANDYMAN_HUB } from 'data/handyman-job-pages'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-28'

const PAGE_URL = `https://www.stringssg.com${HANDYMAN_HUB}`
const OG_IMAGE = "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/handyman/handyman-singapore.jpg"

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps() {
  const countryCd = "sg"
  const countryName = "Singapore"

  const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);

  // Both loaders fall back to a committed snapshot, so a failed API call can no
  // longer fail the build or leave ISR stuck on a stale page.
  const requestCategorySettings = await loadHandymanCategorySettings(countryName)
  const handymanPriceList = await loadHandymanPriceList(countryName)

  return {
      props: {
        requestCategorySettings,
        countryName,
        countryCd,
        modifiedDate,
        month,
        year,
        handymanTestimonials,
        handymanPriceList
      },
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
  const requestCategorySettings = props.requestCategorySettings
  const countryName = props.countryName
  const modifiedDate = props.modifiedDate
  const testimonials = props.handymanTestimonials
  const handymanPriceList = props.handymanPriceList

  const items = [...handymanPriceList].sort((a, b) => a.serviceCost - b.serviceCost)
  const from = formatPrice(fromPrice(items))

  // Was an 85-character, keyword-stuffed "24 Hour Emergency" title. Technicians
  // attend 08:00-22:00 (see localBusinessNode openingHours), so "24 hour" is gone.
  const title = `Handyman Singapore | Same-Day, Fixed Prices From ${from}`
  const description = `Affordable handyman service in Singapore for plumbing, electrical, TV mounting, curtains, locks and painting. Fixed prices from ${from}, same-day slots.`

  const schema = buildServiceHubGraph({
    pageUrl: PAGE_URL,
    pageName: title,
    description,
    trail: buildBreadcrumb(HANDYMAN_HUB),
    items,
    serviceName: 'Handyman Services Singapore',
    serviceType: 'Handyman Service',
    image: OG_IMAGE,
    datePublished: '2025-01-01T07:30:46+00:00',
    modifiedDate,
  })

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={description}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"  content={PAGE_URL} />
        <meta property="og:type"   content="website" />
        <meta property="og:title"    content={title}/>
        <meta property="og:description" content={description}/>
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:site_name" content="StringsSG" />
        <meta property="og:image"  content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={PAGE_URL}/>
        <meta name="twitter:title" content={title}/>
        <meta name="twitter:description" content={description}/>
        <meta name="twitter:image" content={OG_IMAGE} /><link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
        <link rel="canonical" href={PAGE_URL} />
      </Head>
        <div>
            <Handyman requestCategorySettings={requestCategorySettings} countryName={countryName} testimonials={testimonials} handymanType="Handyman" handymanPriceList={handymanPriceList}/>
        </div>
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
        />
        {/* The Facebook pixel is mounted globally in _app.js with the same id, so
            the copy that used to be here only re-fired init/PageView. */}
    </>
  )
}
